// BrainContext state machine (PURE reducer + selectors, dependency-free & testable).
//
// The Brain is ONE persistent surface. "States are behaviors, not routes": the state
// only changes the EMPHASIS of the same surface — the graph never remounts, and none
// of these transitions is a navigation event. Conversation is an OVERLAY that rises
// over the Brain and, on dismiss, returns IN PLACE (prior state restored).

export const BRAIN_STATES = { EXECUTION: "execution", REFLECTION: "reflection", CONVERSATION: "conversation" };

export const ACTIONS = {
  SET_STATE: "SET_STATE",
  FOCUS_REC: "FOCUS_REC",
  SELECT_NODE: "SELECT_NODE",
  OPEN_CONVERSATION: "OPEN_CONVERSATION",
  CLOSE_CONVERSATION: "CLOSE_CONVERSATION",
  SET_CAPABILITIES: "SET_CAPABILITIES",
  CACHE_RESOURCE: "CACHE_RESOURCE",
};

export function makeInitialState(overrides = {}) {
  return {
    state: BRAIN_STATES.EXECUTION,
    focusRecId: null,
    graphSelection: null,
    overlay: { conversation: false },
    _prevState: null,
    capabilities: {},
    resourceCache: {},
    ...overrides,
  };
}

export function brainReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_STATE:
      if (action.state === BRAIN_STATES.CONVERSATION) return state; // conversation is via OPEN_CONVERSATION only
      return { ...state, state: action.state };
    case ACTIONS.FOCUS_REC:
      return { ...state, focusRecId: action.focusRecId || null, state: BRAIN_STATES.EXECUTION };
    case ACTIONS.SELECT_NODE:
      return { ...state, graphSelection: action.nodeId || null };
    case ACTIONS.OPEN_CONVERSATION:
      return {
        ...state,
        overlay: { ...state.overlay, conversation: true },
        _prevState: state.state === BRAIN_STATES.CONVERSATION ? state._prevState : state.state,
        state: BRAIN_STATES.CONVERSATION,
        graphSelection: action.nodeId != null ? action.nodeId : state.graphSelection,
      };
    case ACTIONS.CLOSE_CONVERSATION:
      return {
        ...state,
        overlay: { ...state.overlay, conversation: false },
        state: state._prevState || BRAIN_STATES.EXECUTION, // returns in place
        _prevState: null,
      };
    case ACTIONS.SET_CAPABILITIES:
      return { ...state, capabilities: { ...state.capabilities, ...(action.capabilities || {}) } };
    case ACTIONS.CACHE_RESOURCE:
      return { ...state, resourceCache: { ...state.resourceCache, [action.key]: action.value } };
    default:
      return state;
  }
}

// The ONE executive recommendation = rank-1 queue item (prefer a real RECOMMENDATION).
export function selectExecutionRec(queueData) {
  const items = (queueData && queueData.items) || [];
  if (!items.length) return null;
  const byRank = items.find((i) => i.rank === 1);
  const top = byRank || items[0];
  return top || null;
}

// Challenge/override is only meaningful for a real recommendation (the /decide write).
export function canChallenge(item) {
  return !!(item && item.item_type === "RECOMMENDATION" && item.item_id);
}
